import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle, faInfoCircle, faWarning, faClock, faBell } from '@fortawesome/free-solid-svg-icons'
import { LoadingSpinner } from '../../shared/components/ui/LoadingSpinner'
import Badge from '../../shared/components/ui/Badge'

// Alert types from monorepo
enum AlertType {
  SENSOR_ALERTS = 'SENSOR_ALERTS',
  SYSTEM_CONTROL_MESSAGES = 'SYSTEM_CONTROL_MESSAGES',
  TEST_MESSAGES = 'TEST_MESSAGES',
}

// Alert interface based on API response
interface Alert {
  id: number
  deviceId: number
  alertType?: string
  message: string
  contact?: string
  created?: string
}

const alertTypeIcons = {
  [AlertType.SENSOR_ALERTS]: faExclamationCircle,
  [AlertType.SYSTEM_CONTROL_MESSAGES]: faInfoCircle,
  [AlertType.TEST_MESSAGES]: faWarning,
}

const alertTypeColors = {
  [AlertType.SENSOR_ALERTS]: 'text-red-600 bg-red-100',
  [AlertType.SYSTEM_CONTROL_MESSAGES]: 'text-blue-600 bg-blue-100',
  [AlertType.TEST_MESSAGES]: 'text-yellow-600 bg-yellow-100',
}

export function AlertsList() {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // TODO: Replace with actual API call from webapp services when available
  useEffect(() => {
    const fetchAlerts = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Placeholder: This should be replaced with actual API call
        // Example: const response = await api.getAlerts()
        // For now, we'll just set empty array
        setAlerts([])
      } catch (err: any) {
        setError(err.message || 'Failed to load alerts')
      } finally {
        setIsLoading(false)
      }
    }

    fetchAlerts()
  }, [])

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-600">Failed to load alerts: {error}</div>
        </div>
      </div>
    )
  }

  if (alerts.length === 0) {
    return (
      <div className="p-6 text-center">
        <FontAwesomeIcon icon={faBell} className="h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Alerts</h3>
        <p className="text-gray-600">
          All your devices are operating normally. Active alerts will appear here when triggered.
        </p>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="space-y-4">
        {alerts.map((alert) => (
          <div key={alert.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div
                  className={`p-2 rounded-full ${alert.alertType ? alertTypeColors[alert.alertType as AlertType] : 'text-gray-600 bg-gray-100'}`}
                >
                  <FontAwesomeIcon
                    icon={alert.alertType ? alertTypeIcons[alert.alertType as AlertType] : faExclamationCircle}
                    className="h-4 w-4"
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-900">Device ID: {alert.deviceId}</p>
                    <Badge variant="default" size="sm">
                      {alert.alertType?.replace(/_/g, ' ') || 'Unknown'}
                    </Badge>
                  </div>

                  <p className="mt-1 text-sm text-gray-600">{alert.message}</p>

                  <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                    <span className="flex items-center">
                      <FontAwesomeIcon icon={faClock} className="mr-1 h-3 w-3" />
                      Created: {alert.created ? new Date(alert.created).toLocaleString() : 'Unknown'}
                    </span>
                    <span>Contact: {alert.contact || 'Unknown'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
